/* eslint-disable default-case */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { Icon } from '../icon/Icon';
import { Link } from 'react-router-dom';
import { rgba } from 'polished';

const ConditionalLink = ({ children, to, condition }) =>
  !condition ? (
    <Link
      to={{
        pathname: to.pathname,
        state: {
          loginRedirect: to.state.loginRedirect ? to.state.loginRedirect : '',
        },
      }}
    >
      {children}
    </Link>
  ) : (
    <a href={to.pathname}>{children}</a>
  );

/**
 * Primary UI component for user interaction
 */
export const Button = ({
  onClick,
  size,
  type,
  icon,
  iconType,
  customLabel,
  materialLabel,
  label,
  link,
  loginRedirect,
  ...props
}) => {
  const isExternalLink = /^(?:[a-z]+:)?\/\//i.test(link);
  return (
    <ClicButton
      type={type}
      size={size}
      iconType={iconType}
      onClick={onClick}
      {...props}
    >
      <ConditionalLink
        to={{
          pathname: link,
          state: { loginRedirect: loginRedirect ? loginRedirect : '' },
        }}
        condition={isExternalLink}
      >
        {icon && (
          <IconSpan type={type} iconType={iconType}>
            <Icon
              type={iconType}
              buttonType={type}
              size={size}
              customLabel={customLabel}
              materialLabel={materialLabel}
            />
          </IconSpan>
        )}
        <Label type={type}>{label}</Label>
      </ConditionalLink>
    </ClicButton>
  );
};

const Label = styled.span`
  ${(props) =>
    props.type === 'tertiary' &&
    `
    text-decoration: underline;
    text-underline-offset: 2px;
  `}
`;

const IconSpan = styled.span`
  margin-left: -8px;
  margin-right: 10px;
`;

const ClicButton = styled.div`
  display: flex;
  a {
    display: flex;
    align-items: center;
    text-align: center;
    font-family: 'Gotham Narrow SSm 4r', 'Gotham Narrow SSm A',
      'Gotham Narrow SSm B', 'HelveticaNeue-Regular', 'Helvetica Neue Regular',
      'Helvetica Neue', Helvetica, Verdana, sans-serif;
    font-weight: 700;
    border: 0;
    border-radius: 100vw;
    cursor: pointer;
    letter-spacing: -0.015em;
    line-height: 1;
    text-decoration: none;
    transition: none;
    &:hover {
      box-shadow: none !important;
    }

    ${(props) =>
      props.size === 'small' &&
      `
      font-size: 14px;
      padding: 8px 26px;
    `}

    ${(props) =>
      props.size === 'medium' &&
      `
      font-size: 16px;
      padding: 12px 32px;
    `}

    ${(props) =>
      props.size === 'large' &&
      `
      font-size: 20px;
      padding: 14px 42px;
      `}

    // primary styles
    ${(props) =>
      props.type === 'primary' &&
      `
      background: linear-gradient(266.92deg, #FABB54 0%, ${
        props.theme.color.primaryFull
      } 100%);
      box-shadow: ${
        props.theme.name === 'light'
          ? `0px 2px 4px rgba(110, 79, 28, 0.1), 0px 8px 16px ${rgba(
              props.theme.color.primaryFull,
              0.15
            )}`
          : `0px 2px 4px rgba(110, 79, 28, 0.1), 0px 8px 16px ${rgba(
              props.theme.color.primaryFull,
              0.2
            )}`
      };
      ${Label} {
        color: ${rgba(props.theme.color.blackFull, 0.8)};
      };
      ${IconSpan} span {
        opacity: .8;
      }
      &:hover {
        background: ${props.theme.color.primaryFull};
        box-shadow: ${
          props.theme.name === 'light'
            ? `0px 4px 8px rgba(110, 79, 28, 0.2), 0px 16px 32px -8px ${rgba(
                props.theme.color.primaryFull,
                0.3
              )}`
            : `0px 4px 8px rgba(110, 79, 28, 0.2), 0px 16px 32px -8px ${props.theme.color.primary50}`
        };
        ${Label} {
          color: ${props.theme.color.blackFull};
        };
        ${IconSpan} span {
          opacity: 1;
        }
      }
      &:active {
        background: linear-gradient(266.92deg, #FABB54 0%, ${
          props.theme.color.primaryFull
        } 100%);
        box-shadow: none;
        ${Label} {
          color: ${rgba(props.theme.color.blackFull, 0.8)};
        };
        ${IconSpan} span {
          opacity: .8;
        }
      }
      &:focus-visible {
        background: ${props.theme.color.primaryFull};
        outline: 2px solid
        ${
          props.theme.name === 'light'
            ? props.theme.color.statusLightFocus
            : props.theme.color.statusDarkFocus
        };
        outline-offset: 4px;
        ${Label} {
          color: ${props.theme.color.blackFull};
        };
        ${IconSpan} span {
          opacity: 1;
        }
      }
      &:focus:not(:focus-visible) {
        outline: none;
      }
      &:disabled,
      &[disabled] {
          box-shadow: none;
          pointer-events: none;
          ${Label} {
            color: ${props.theme.color.black50};
          };
          ${IconSpan} span {
            opacity: .8;
          }
        }
  `}
  
  // secondary styles
  ${(props) =>
      props.type === 'secondary' &&
      `
      background-color: ${props.theme.color.black10};
      background: ${
        props.theme.name === 'light'
          ? props.theme.color.white50
          : props.theme.color.black50
      };
      border: 1px solid
        ${
          props.theme.name === 'light'
            ? `${rgba(props.theme.color.blackFull, 0.35)}`
            : props.theme.color.white50
        };
      backdrop-filter: blur(30px);
      ${Label} {
        color: ${
          props.theme.name === 'light'
            ? props.theme.color.black90
            : props.theme.color.white90
        };
      };
      ${IconSpan} span {
        opacity: .75;
      }
      &:hover {
        background: ${
          props.theme.name === 'light'
            ? props.theme.color.white50
            : props.theme.color.black50
        };
        border: 1px solid
          ${
            props.theme.name === 'light'
              ? `${rgba(props.theme.color.blackFull, 0.65)}`
              : `${rgba(props.theme.color.whiteFull, 0.65)}`
          };
        ${Label} {
          color: ${
            props.theme.name === 'light'
              ? props.theme.color.blackFull
              : props.theme.color.whiteFull
          }
        };
        ${IconSpan} span {
          ${props.theme.name === 'light' ? `opacity: .9` : `opacity: 1`}
        }
      }
      &:active {
        background: ${
          props.theme.name === 'light'
            ? props.theme.color.white50
            : props.theme.color.black50
        };
        border: 1px solid
          ${
            props.theme.name === 'light'
              ? props.theme.color.black10
              : props.theme.color.white10
          };
        ${Label} {
          color: ${
            props.theme.name === 'light'
              ? props.theme.color.black90
              : props.theme.color.white90
          }
        };
        ${IconSpan} span {
          opacity: .75;
        }
      }
      &:focus-visible {
        background: ${
          props.theme.name === 'light'
            ? props.theme.color.white50
            : props.theme.color.black50
        };
        border: 1px solid transparent;
        outline: 2px solid
          ${
            props.theme.name === 'light'
              ? props.theme.color.statusLightFocus
              : props.theme.color.statusDarkFocus
          };
        ${Label} {
          color: ${
            props.theme.name === 'light'
              ? props.theme.color.blackFull
              : props.theme.color.whiteFull
          };
        };
        ${IconSpan} span {
          ${props.theme.name === 'light' ? `opacity: .9` : `opacity: 1`}
        }
      }
      &:focus:not(:focus-visible) {
        outline: none;
      }
      &:disabled,
      &[disabled] {
        background: ${
          props.theme.name === 'light'
            ? props.theme.color.white50
            : `${rgba(props.theme.color.blackFull, 0.25)}`
        };
        border: 1px solid
          ${
            props.theme.name === 'light'
              ? `${(props.theme.color.blackFull, 0.15)}`
              : props.theme.color.white50
          };
        pointer-events: none;
        ${Label} {
          color: ${
            props.theme.name === 'light'
              ? props.theme.color.black75
              : props.theme.color.white75
          }
        };
        ${IconSpan} span {
          opacity: .5;
        }
      }
  `}

  // tertiary styles
  ${(props) =>
      props.type === 'tertiary' &&
      `
    ${Label} {
      color: ${
        props.theme.name === 'light'
          ? props.theme.color.black90
          : props.theme.color.white90
      };
    }
    background-color: transparent;
    text-transform: capitalize;
    backdrop-filter: none;
    ${IconSpan} span {
      opacity: .75;
    }
    &:hover {
      ${Label} {
        color: ${
          props.theme.name === 'light'
            ? props.theme.color.blackFull
            : props.theme.color.whiteFull
        };
      };
      ${IconSpan} span {
        ${props.theme.name === 'light' ? `opacity: .9` : `opacity: 1`}
      }
    }
    &:active {
      ${Label} {
        color: ${
          props.theme.name === 'light'
            ? props.theme.color.black90
            : props.theme.color.whiteFull
        };
        text-decoration: none;
      }
      ${IconSpan} span {
        opacity: .75;
      }
    }
    &:focus-visible {
      ${Label} {
        color: ${
          props.theme.name === 'light'
            ? props.theme.color.blackFull
            : props.theme.color.whiteFull
        }
      };
      ${IconSpan} span {
        ${props.theme.name === 'light' ? `opacity: .9` : `opacity: 1`}
      }
      box-shadow: ${props.theme.focusRing};
      outline: none;
    }
    &:focus:not(:focus-visible) {
      ${Label} {
        color: ${
          props.theme.name === 'light'
            ? props.theme.color.black75
            : props.theme.color.white75
        };
      }
      outline: none;
    }
    &:disabled,
    &[disabled] {
      pointer-events: none;
      ${Label} {
        color: ${
          props.themeName === 'light'
            ? props.theme.color.black75
            : props.theme.color.white75
        };
        text-decoration: none;
      }
      ${IconSpan} span {
        opacity: .5;
      }
    }
  `}
  }
`;

Button.propTypes = {
  disabled: PropTypes.bool,
  handleClick: PropTypes.func,
  label: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  type: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  icon: PropTypes.bool,
  iconType: PropTypes.oneOf(['custom', 'material']),
  customLabel: PropTypes.oneOf([
    'add_circle_outline',
    'add',
    'alarm',
    'arrow_back',
    'arrow_drop_down',
    'cancel',
    'chat_bubble',
    'check_box_outline_blank',
    'check_box',
    'check_circle_outline',
    'check-orange',
    'check-white',
    'check',
    'chevron_left',
    'chevron_right',
    'circle_add',
    'circle_close',
    'circle_facebook',
    'circle_google',
    'circle_linkedin',
    'circle_pinterest',
    'circle_remove',
    'circle_share',
    'circle_twitter',
    'clic_play_error',
    'clic_play_lock',
    'clic_play',
    'close_white',
    'close',
    'email',
    'error_red',
    'error_white',
    'error',
    'event',
    'expand_less',
    'expand_more',
    'facebook',
    'favorite_outline',
    'favorite',
    'first_page',
    'forum',
    'google_plus',
    'googleplus',
    'home',
    'image',
    'instagram',
    'last_page',
    'layers_white',
    'layers',
    'linked_in',
    'linkedin',
    'list',
    'live_tv',
    'lock_open',
    'lock',
    'menu',
    'navigate_before',
    'navigate_next',
    'person',
    'pinterest',
    'play_arrow',
    'play_circle_outline',
    'remove_circle_outline',
    'search',
    'share',
    'sign',
    'subtitles',
    'swap_horiz',
    'timer',
    'toggle_cross',
    'toggle_tick',
    'tv',
    'twitter',
    'warning',
    'youtube',
  ]),
  materialLabel: PropTypes.oneOf([
    'remove_circle_outline',
    'control_point',
    'check_box',
    'check_box_outline_blank',
    'keyboard',
    'arrow_back_ios',
    'arrow_forward_ios',
    'share',
    'radio_button_checked',
    'search',
    'close',
    'home',
    'replay',
    'bookmark',
    'tv',
    'event',
    'layers',
    'navigate_before',
    'navigate_next',
  ]),
};

Button.defaultProps = {
  disabled: false,
  handleClick: undefined,
  label: 'button',
  size: 'medium',
  type: 'primary',
  icon: false,
  iconType: 'custom',
  customLabel: 'alarm',
  materialLabel: 'close',
};
